import { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Form } from 'antd'
import { Popup } from 'components/Popup'
import { Button, BUTTON_TYPES } from 'components/Button'
import { BTN_TXT, PLACEHOLDERS } from 'constants/txt'
import { Typography, TYPOGRAPHY_WEIGHT } from 'components/Typography'
import { Table } from 'components/Table'
import { setLoading } from 'redux/store/common/slice'
import { getUser } from 'redux/store/user/getters'
import { columns } from './constants'
import { fullFormatPhoneNumber, getUserCommunity } from 'helper/common'
import { ability } from 'features/Permission/ability'
import { ACTIONS, Can, SUBJECTS } from 'features/Permission'
import { HOSPITAL_STATUSES } from 'constants/common'
import { DEFAULT_PAGE } from 'components/Table/constants'
import { ReactComponent as Edit16 } from 'assets/svg/Edit16.svg'
import { ReactComponent as Delete16 } from 'assets/svg/Delete16.svg'
import { ReactComponent as Plus16 } from 'assets/svg/Plus16.svg'
import {
  THospital,
  THospitalsFilters,
  THospitalsFiltersFunc,
  THospitalsParams,
  THospitalsTableRequestData
} from './types'
import { useDebounce } from 'hooks/hooks'
import { Input } from 'components/Input'
import { ReactComponent as Close16 } from 'assets/svg/Clos16.svg'
import { ReactComponent as Search16 } from 'assets/svg/Search16.svg'
import {
  archiveHospitals,
  getHospitals
} from '../../../../HealthSystemDetails/HospitalsTable/api'
import {
  archiveHospitalsOutsideCommunity,
  getHospitalsOutsideCommunity
} from '../../../MyOrganizationCommunity/api'
import { useHSDetailsContext } from '../Providers/HSContextProvider'
import { useParams } from 'react-router-dom'
import { routes } from '../../../../../router'
import useRouter from '../../../../../hooks/useRouter'
import './styles.scss'
import { ActionModal } from 'features/MyCommunity/Hospitals/EditHospitalDetails/ActionModal'

const getHospitalDetailsPath = (
  hospitalId: string,
  hsId?: string,
  outsideCommunityMode?: boolean
) =>
  outsideCommunityMode
    ? `${routes.myCommunity}${routes.myOrganization}/hospitals/${hospitalId}`
    : `${routes.myCommunity}${routes.myOrganization}/health_systems/${hsId}/hospitals/${hospitalId}`

export const HospitalsTable = ({ hsName, canEditHS }) => {
  const { id: hsId } = useParams()
  const {
    state: { outsideCommunityMode }
  } = useHSDetailsContext()
  const { push } = useRouter()
  const [selectedRows, setSelectedRows] = useState<THospital[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])
  const [sortParams, setSortParams] = useState<THospitalsTableRequestData>({})
  const [tableData, setTableData] = useState<THospital[]>([])
  const [filters, setFilters] = useState<THospitalsFilters>()
  const [totalNumber, setTotalNumber] = useState(0)
  const [openArchivePopup, setOpenArchivePopup] = useState(false)
  const [openActivatePopup, setOpenActivatePopup] = useState(false)
  const [isDiscardChanges, setDiscardChanges] = useState(false)
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const [searchValue, setSearchValue] = useState('')
  const [firstLoad, setFirstLoad] = useState(true)
  const user = useSelector(getUser)
  const effectiveHsId = outsideCommunityMode ? user.health_system : hsId
  const debouncedSearch = useDebounce(searchValue, 500)
  const dispatch = useDispatch()
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const [form] = Form.useForm()
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(
      () => {
        getTableData({ ...sortParams, ...filters, page: { ...DEFAULT_PAGE } })
        setPageInfo({ ...DEFAULT_PAGE })
      },
      firstLoad ? 0 : 500
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  const canViewBankEnrolledColumn = ability.can(
    ACTIONS.VIEW,
    SUBJECTS.BANK_ENROLLED_IN_HOSPITALS_TABLE
  )

  const canViewDepartments = ability.can(
    ACTIONS.VIEW,
    SUBJECTS.DEPARTMENTS_IN_HS_DETAILS
  )

  const getTableData = ({
    sortField,
    sortOrder,
    filters,
    page
  }: THospitalsTableRequestData) => {
    const communityOrHsId = getUserCommunity(user)
    if (!communityOrHsId || !effectiveHsId) return
    dispatch(setLoading(true))
    const params: THospitalsParams = { ...filters }
    if (sortField) {
      if (sortOrder) {
        params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
      }
      setSortParams({
        sortField,
        sortOrder
      })
    }
    if (searchValue) {
      params.search = searchValue
    }
    const dataPage = page ? page : pageInfo
    params.limit = dataPage.pageSize
    params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
    const hospitalsRequest = outsideCommunityMode
      ? getHospitalsOutsideCommunity(effectiveHsId, params)
      : getHospitals(communityOrHsId, effectiveHsId, params)
    hospitalsRequest
      .then((resp) => {
        if (!resp.data?.results) {
          return
        }
        setTableData(
          resp.data.results.map((i) => ({
            ...i,
            phone_number: fullFormatPhoneNumber(i.phone_number),
            key: i.uuid
          }))
        )
        setTotalNumber(resp.data.count)
        if (firstLoad) {
          setFirstLoad(false)
        }
      })
      .finally(() => dispatch(setLoading(false)))
  }
  const handleTableChange = (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      filters,
      page
    })
    setPageInfo(page)
  }
  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    getTableData({
      ...sortParams,
      filters,
      page
    })
    setPageInfo(page)
  }
  const applyFilters: THospitalsFiltersFunc = (field, appliedFilters) => {
    const newFilters = { ...filters, [field]: appliedFilters }
    setFilters(newFilters)
    getTableData({ ...sortParams, filters: newFilters })
  }
  const handleSelectRow = (selectedRowKeys, selectedRows) => {
    setSelectedRowKeys(selectedRowKeys)
    setSelectedRows(selectedRows)
  }
  const handleArchiveHospitals = () => {
    const communityOrHsId = getUserCommunity(user)
    if (!communityOrHsId || !effectiveHsId) return
    dispatch(setLoading(true))
    const archiveRequest = outsideCommunityMode
      ? archiveHospitalsOutsideCommunity(effectiveHsId, {
          hospitals: selectedRowKeys
        })
      : archiveHospitals(communityOrHsId, effectiveHsId, {
          hospitals: selectedRowKeys
        })
    archiveRequest
      .then(() => {
        setOpenArchivePopup(false)
        setSelectedRowKeys([])
        setSelectedRows([])
        getTableData({ ...sortParams, filters })
      })
      .catch(() => dispatch(setLoading(false)))
  }

  const handleCloseAddEditHospitalPopup = () => {
    setDiscardChanges(false)
    setSelectedRowKeys([])
    setSelectedRows([])
    form.resetFields()
  }

  const handleNavigate = () => {
    push(getHospitalDetailsPath('new', effectiveHsId, outsideCommunityMode), {
      state: { hsName: hsName }
    })
  }
  const handleEditNavigation = () => {
    push(
      getHospitalDetailsPath(
        selectedRowKeys[0],
        effectiveHsId,
        outsideCommunityMode
      ),
      {
        state: { hsName: hsName }
      }
    )
  }

  const triggerHospitalData = () => {
    setSelectedRows([])
    setSelectedRowKeys([])
    getTableData({ ...sortParams, filters })
  }
  return (
    <>
      <div className="hs-details__tables-menu row justify-between mb-4">
        {!!selectedRowKeys.length ? (
          <Typography.Body1
            className="table-wrapper__select-title"
            weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}
          >
            Selected {selectedRowKeys.length} of {totalNumber}
          </Typography.Body1>
        ) : (
          <Input
            className="vendor-users-list__search allow-clear"
            propsInput={{
              allowClear: { clearIcon: <Close16 /> },
              placeholder: PLACEHOLDERS.SEARCH,
              prefix: <Search16 />,
              value: searchValue,
              onChange: (e) => setSearchValue(e.target.value)
            }}
          />
        )}
        <div className="hs-details__tables-actions">
          <Can I={ACTIONS.CRUD} a={SUBJECTS.HOSPITAL}>
            {selectedRowKeys.length === 1 && (
              <Button
                type={BUTTON_TYPES.PRIMARY}
                icon={<Edit16 />}
                onClick={handleEditNavigation}
                upperCase
                className="hs-details__tables-actions__edit-btn"
              >
                {BTN_TXT.EDIT}
              </Button>
            )}
            {!!selectedRows.length &&
              selectedRows.filter(
                (i) => i.status !== HOSPITAL_STATUSES.ARCHIVED
              ).length === selectedRows.length && (
                <Button
                  type={BUTTON_TYPES.PRIMARY}
                  icon={<Delete16 />}
                  onClick={() => setOpenArchivePopup(true)}
                  upperCase
                  danger
                >
                  {BTN_TXT.ARCHIVE}
                </Button>
              )}
            {selectedRowKeys.length === 1 &&
              selectedRows[0]?.status === HOSPITAL_STATUSES.ARCHIVED && (
                <Button
                  type={BUTTON_TYPES.DEFAULT}
                  onClick={() => setOpenActivatePopup(true)}
                  upperCase
                  className="hs-details__tables-actions__edit-btn"
                >
                  {BTN_TXT.RE_ACTIVATE}
                </Button>
              )}
            {!selectedRowKeys.length && (
              <Button
                type={BUTTON_TYPES.PRIMARY}
                icon={<Plus16 />}
                onClick={handleNavigate}
                upperCase
              >
                {BTN_TXT.ADD_HOSPITAL}
              </Button>
            )}
          </Can>
        </div>
      </div>
      <Table<THospital>
        dataSource={tableData}
        onChange={handleTableChange}
        className="hospitals-list-table"
        columns={columns({
          applyFilters,
          filters,
          communityId: getUserCommunity(user) ?? '',
          hsId: effectiveHsId ?? '',
          handleSelectRow,
          setOpenArchivePopup,
          setOpenActivatePopup,
          canEditHS,
          user,
          canViewBankEnrolledColumn,
          canViewDepartments,
          outsideCommunityMode
        })}
        onChangePage={handleChangePageSize}
        pageSize={pageInfo.pageSize}
        pagination={{
          pageSize: pageInfo.pageSize,
          current: pageInfo.pageNumber,
          total: totalNumber
        }}
        createButton={{
          buttonText: BTN_TXT.ADD_HOSPITAL,
          availability: ability.can(ACTIONS.CRUD, SUBJECTS.HOSPITAL),
          action: handleNavigate
        }}
        rowSelection={
          ability.can(ACTIONS.CRUD, SUBJECTS.HOSPITAL)
            ? {
                onChange: handleSelectRow,
                selectedRowKeys
              }
            : undefined
        }
      />
      <Popup
        visible={openArchivePopup}
        onCancel={() => setOpenArchivePopup(false)}
        title={`Archive ${
          selectedRows.length === 1 ? selectedRows[0].name : 'hospitals'
        }?`}
        wrapClassName="popup-over"
        width={442}
        footer={
          <>
            <Button
              onClick={() => setOpenArchivePopup(false)}
              type={BUTTON_TYPES.DEFAULT}
              upperCase
            >
              {BTN_TXT.CANCEL}
            </Button>
            <Button
              type={BUTTON_TYPES.PRIMARY}
              onClick={handleArchiveHospitals}
              upperCase
              danger
            >
              {BTN_TXT.ARCHIVE}
            </Button>
          </>
        }
      >
        <Typography.Body1>
          You are about to archive selected Hospitals:
          <br />
          <ul className="archive-hospitals-confirmation">
            {selectedRows?.map((item) => {
              return <li key={item.uuid}>{item.name}</li>
            })}
          </ul>
          Users may have active contracts associated with the selected
          Hospitals. Do you want to archive them?
        </Typography.Body1>
      </Popup>
      {openActivatePopup && (
        <ActionModal
          details={selectedRows[0]}
          handleHospitalForEdit={triggerHospitalData}
          openModal={openActivatePopup}
          setOpenModal={setOpenActivatePopup}
          outsideCommunityMode={outsideCommunityMode}
        />
      )}
      <Popup.Discard
        visible={isDiscardChanges}
        onCancel={() => setDiscardChanges(false)}
        onSubmit={handleCloseAddEditHospitalPopup}
      />
    </>
  )
}
